import React from "react";
import { styled, Theme } from "@mui/system";
import {
  Typography,
  Button,
  Grid,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { isMobile, isTablet } from "react-device-detect";
import ReactRouterLink from "./ReactRouterLink";
import { motion } from "framer-motion";
import { scrollToElement } from "../../utils";

// Icons
import VerifiedIcon from "@mui/icons-material/Verified";
import GppGoodIcon from "@mui/icons-material/GppGood";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

// Animation settings
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

// Styled container
const HeroContainer = styled("div")(({ theme }: { theme: Theme }) => ({
  backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url("/fp-hero-bkg.png")`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundAttachment: "fixed",

  paddingTop: theme.spacing(6),
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  textAlign: "center",
}));

const OverlayBox = styled(motion.div)(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  borderRadius: "20px",
  padding: theme.spacing(4),
  maxWidth: "600px",
  width: "100%",
  boxShadow: "0px 8px 20px rgba(0,0,0,0.4)",
}));

const StartSellingButton = styled(Button)({
  backgroundColor: "#e78758",
  color: "#fff",
  borderRadius: "30px",
  padding: "10px 24px",
  fontWeight: 600,
  "&:hover": {
    backgroundColor: "#c56a3e",
  },
});

const BrowseToysButton = styled(Button)({
  backgroundColor: "#ffffffcc",
  color: "#000",
  borderRadius: "30px",
  padding: "10px 24px",
  fontWeight: 600,
  "&:hover": {
    backgroundColor: "#ffffff",
  },
});

const BadgeIcon = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  minWidth: "130px",
  gap: "8px",
  color: "#fff",
  opacity: 0.85,
});

const HeroComponent: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleScrollToProducts = () => {
    scrollToElement("products-section");
  };

  return (
    <HeroContainer>
      <OverlayBox
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
      >
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: "1.75rem", sm: "2.5rem" },
            fontWeight: 700,
            mb: 2,
          }}
        >
          Your Toys Still Have a Story.
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: "1rem", sm: "1.25rem" },
            color: "#ffffffcc",
            mb: 4,
          }}
        >
          Buy, sell, and rediscover toys from every era. Whether you’re a
          serious collector or just clearing out the closet—you’re home.
        </Typography>

        <Grid container spacing={2} justifyContent="center" sx={{ mb: 2 }}>
          <Grid item>
            <ReactRouterLink
              to="/blog/start-selling-on-fiddlepiddlecom"
              isLinkUnderline={false}
              linkElement={
                <StartSellingButton variant="contained">
                  Start Selling
                </StartSellingButton>
              }
            />
          </Grid>
          <Grid item>
            <BrowseToysButton onClick={handleScrollToProducts}>
              Browse Toys
            </BrowseToysButton>
          </Grid>
        </Grid>

        <Typography
          variant="body2"
          sx={{
            fontSize: "0.95rem",
            color: "#ffffffb3",
            mb: 2,
            fontStyle: "italic",
          }}
        >
          It’s free to list. No BS.
        </Typography>

        <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
          <Grid item xs={6} sm={3}>
            <BadgeIcon>
              <VerifiedIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 1 }}>
                Verified
              </Typography>
            </BadgeIcon>
          </Grid>
          <Grid item xs={6} sm={3}>
            <BadgeIcon>
              <GppGoodIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 1 }}>
                Protected
              </Typography>
            </BadgeIcon>
          </Grid>
          <Grid item xs={6} sm={3}>
            <BadgeIcon>
              <RocketLaunchIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 1 }}>
                Fast Setup
              </Typography>
            </BadgeIcon>
          </Grid>
          <Grid item xs={6} sm={3}>
            <BadgeIcon>
              <LocalShippingIcon fontSize="small" />
              <Typography variant="caption" sx={{ ml: 1 }}>
                Easy Shipping
              </Typography>
            </BadgeIcon>
          </Grid>
        </Grid>
      </OverlayBox>
    </HeroContainer>
  );
};

export default HeroComponent;
